import {
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useIntl } from 'react-intl'

import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import TextButton from 'LuxKit/Button/TextButton'
import LinePatioIcon from 'LuxKit/Icons/line/LinePatioIcon'
import Group from 'components/utils/Group'
import TrackedCardCarousel from 'components/Common/TrackedCardCarousel/TrackedCardCarousel'
import CruiseItineraryAttractionCard from './CruiseItineraryAttractionCard'
import { formatPlacePhrase } from '@luxuryescapes/lib-location-grammar'

const INITIAL_SHIP_CATEGORY_COUNT = 2

const ATTRACTION_CAROUSEL_GAP = 12

const DEFAULT_ATTRACTIONS: Array<App.Cruises.ItineraryAttraction> = []
const DEFAULT_SHIP_AMENITIES: Array<App.Cruises.ShipAmenity> = []

interface Props {
  portName: string
  attractions?: Array<App.Cruises.ItineraryAttraction>
  shipAmenities?: Array<App.Cruises.ShipAmenity>
  showShipAmenities: boolean
}

function CruiseItineraryPointsOfInterest({
  portName,
  attractions = DEFAULT_ATTRACTIONS,
  shipAmenities = DEFAULT_SHIP_AMENITIES,
  showShipAmenities,
}: Props) {
  const intl = useIntl()
  const [showAllShipCategories, setShowAllShipCategories] = useState(false)

  const shipAmenitiesByCategory = useMemo(() => {
    const map = new Map<string, Array<App.Cruises.ShipAmenity>>()
    for (const amenity of shipAmenities) {
      const category = amenity.category?.trim() || 'Other'
      const existing = map.get(category)
      if (existing) {
        existing.push(amenity)
      } else {
        map.set(category, [amenity])
      }
    }
    return Array.from(map.entries()).sort(([catA, listA], [catB, listB]) => {
      const byCount = listB.length - listA.length
      if (byCount !== 0) {
        return byCount
      }
      return catA.localeCompare(catB)
    })
  }, [shipAmenities])

  const displayedShipCategories = useMemo(() => {
    if (showAllShipCategories || shipAmenitiesByCategory.length <= INITIAL_SHIP_CATEGORY_COUNT) {
      return shipAmenitiesByCategory
    }
    return shipAmenitiesByCategory.slice(0, INITIAL_SHIP_CATEGORY_COUNT)
  }, [shipAmenitiesByCategory, showAllShipCategories])

  const hasMoreShipCategories = shipAmenitiesByCategory.length > INITIAL_SHIP_CATEGORY_COUNT

  const viewMoreShipCategoriesLabel = intl.formatMessage({
    id: '5nqFJM6RzdBgBbhPiQln',
    defaultMessage: 'View More',
    description: 'Button text to view more top cruise offers in the cruise section',
  })

  const viewLessShipCategoriesLabel = intl.formatMessage({
    id: '99xKxz6TosCKSaEMRM4D',
    defaultMessage: 'View less',
    description: 'Button to collapse and show fewer dream block items',
  })

  const pointsOfInterestHeading = useMemo(
    () =>
      intl.formatMessage(
        {
          id: 'Gu7L7DjKeSfJizcB0Bsu',
          defaultMessage: 'Points of interest in <bold>{placeName}</bold>',
          description:
            'Cruise itinerary section title for shore attractions near the selected port; port name is visually emphasised',
        },
        {
          placeName: formatPlacePhrase({ location: { name: portName }, language: intl.locale, intent: 'location' }),
          bold: (chunks: ReactNode) => <b key="poi-port-name">{chunks}</b>,
        },
      ),
    [intl, portName],
  )

  const pointsOfInterestIntro = useMemo(
    () =>
      intl.formatMessage({
        id: 'WPX7dskwiFbFnsGWP57w',
        defaultMessage:
          'Discover the top sights, landmarks, and attractions to explore near the port during your time ashore.',
        description:
          'Introductory sentence above the cruise itinerary points-of-interest carousel for a port day',
      }),
    [intl],
  )

  const exploreShipHeading = useMemo(
    () =>
      intl.formatMessage({
        id: 'xWEFBlCZldecxGk1glfR',
        defaultMessage: 'Explore the ship',
        description:
          'Section heading on cruise itinerary at-sea days for onboard ship amenities grouped by category',
      }),
    [intl],
  )

  const shipCategoriesResetKey = useMemo(
    () => shipAmenitiesByCategory.map(([category]) => category).join('|'),
    [shipAmenitiesByCategory],
  )

  useEffect(() => {
    setShowAllShipCategories(false)
  }, [shipCategoriesResetKey])

  if ((!showShipAmenities && attractions.length === 0) || (showShipAmenities && shipAmenities.length === 0)) {
    return null
  }

  return (
    <>
      {!showShipAmenities && attractions.length > 0 && (
        <Group direction="vertical" gap={16}>
          <Group direction="vertical" gap={8}>
            <Group direction="horizontal" gap={4} verticalAlign="center">
              <LinePatioIcon size="S" />
              <Heading variant="heading4">
                {pointsOfInterestHeading}
              </Heading>
            </Group>
            <BodyText variant="medium">
              {pointsOfInterestIntro}
            </BodyText>
          </Group>

          <TrackedCardCarousel pageSize="auto" snap="start" gap={ATTRACTION_CAROUSEL_GAP}>
            {attractions.map(attraction => (
              <CruiseItineraryAttractionCard
                key={attraction.name}
                imageId={attraction.imageId}
                imageAlt={attraction.name}
                modalTitle={attraction.name}
                modalContent={
                  <BodyText variant="medium">{attraction.description}</BodyText>
                }
              >
                <Group direction="vertical" gap={4}>
                  <BodyText variant="medium" weight="bold">
                    {attraction.name}
                  </BodyText>
                  <Caption variant="large" colour="neutral-three" lineClamp={3}>
                    {attraction.description}
                  </Caption>
                </Group>
              </CruiseItineraryAttractionCard>
            ))}
          </TrackedCardCarousel>
        </Group>
      )}

      {showShipAmenities && shipAmenities.length > 0 && (
        <Group direction="vertical" gap={24}>
          <Heading variant="heading4">
            {exploreShipHeading}
          </Heading>
          {displayedShipCategories.map(([category, amenitiesInCategory]) => (
            <Group key={category} direction="vertical" gap={16}>
              <Heading variant="heading5" format="titlecase">
                {category}
              </Heading>
              <TrackedCardCarousel pageSize="auto" snap="start" gap={ATTRACTION_CAROUSEL_GAP}>
                {amenitiesInCategory.map(amenity => (
                  <CruiseItineraryAttractionCard
                    key={`${category}-${amenity.name}`}
                    imageId={amenity.imageId}
                    imageAlt={amenity.name}
                    modalTitle={amenity.name}
                    modalContent={
                      <>
                        <Caption variant="large" colour="neutral-three" weight="bold">
                          {amenity.category}
                        </Caption>
                        <BodyText variant="medium">{amenity.description}</BodyText>
                      </>
                    }
                  >
                    <Group direction="vertical" gap={4}>
                      <BodyText variant="medium" weight="bold">
                        {amenity.name}
                      </BodyText>
                      <Caption variant="large" colour="neutral-three" lineClamp={3}>
                        {amenity.description}
                      </Caption>
                    </Group>
                  </CruiseItineraryAttractionCard>
                ))}
              </TrackedCardCarousel>
            </Group>
          ))}
          {hasMoreShipCategories && (
            <Group direction="horizontal" horizontalAlign="center">
              <TextButton kind="tertiary" onClick={() => setShowAllShipCategories(v => !v)}>
                {showAllShipCategories ? viewLessShipCategoriesLabel : viewMoreShipCategoriesLabel}
              </TextButton>
            </Group>
          )}
        </Group>
      )}
    </>
  )
}

export default CruiseItineraryPointsOfInterest
